import type {
  CallFollowUp,
  CallFollowUpsResponse,
  CallLogItem,
  CallLogsResponse,
} from './types';

/**
 * Pages of an offset-paged list, flattened into one list of rows.
 *
 * Both lists are newest first and paged by offset, so a call that lands while
 * somebody is scrolling pushes every row down one: the first row of page 2 is
 * then the last row of page 1, fetched twice. React would warn about the
 * duplicate key and the grid would draw the call twice, so the first
 * occurrence wins. (The opposite drift — a row closed elsewhere pulls page 2
 * up and one row is never fetched — is repaired by the refetch the same SSE
 * event triggers, which re-reads every loaded page.)
 */
export function flattenPages<TRow>(
  pages: ReadonlyArray<ReadonlyArray<TRow>>,
  keyOf: (row: TRow) => string,
): TRow[] {
  const seen = new Set<string>();
  const out: TRow[] = [];
  for (const page of pages) {
    for (const row of page) {
      const key = keyOf(row);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
  }
  return out;
}

/**
 * The page after this one, or undefined when there is none.
 *
 * An empty page ends the list whatever the pagination claims. Without that, a
 * server whose count ran ahead of its rows (a row deleted between the count
 * and the read) would answer "there is a next page" forever, and a screen
 * that loads more whenever its last row is visible would request it forever.
 */
export function nextCallLogsPage(last: CallLogsResponse | undefined): number | undefined {
  const rows = last?.data?.callLogs ?? [];
  const pagination = last?.data?.pagination;
  if (!pagination || rows.length === 0) return undefined;
  const more = pagination.hasNextPage ?? pagination.currentPage < pagination.totalPages;
  return more ? pagination.currentPage + 1 : undefined;
}

export function nextActionNeededPage(
  last: CallFollowUpsResponse | undefined,
): number | undefined {
  const rows = last?.data?.rows ?? [];
  const pagination = last?.data?.pagination;
  if (!pagination || rows.length === 0) return undefined;
  return pagination.page < pagination.totalPages ? pagination.page + 1 : undefined;
}

export const callLogRowKey = (row: CallLogItem) => row.callControlId;
export const followUpRowKey = (row: CallFollowUp) => row.callControlId;
